import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { hyperliquidClient } from '../services/hyperliquid';

interface UserStateData {
  balance: number;
  accountValue: number;
  marginUsed: number;
  positions: Array<{
    coin: string;
    szi: string;
    entryPx: string;
    unrealizedPnl: string;
    leverage: number;
    liquidationPx: string | null;
    returnOnEquity: string;
    marginUsed: string;
    cumFunding: string;
  }>;
  isLoading: boolean;
  isConnected: boolean;
}

/**
 * Consolidated hook for user state data (balance, positions, etc.)
 * Replaces duplicate getUserState calls across useBalance and PositionsTab
 */
export function useUserState(): UserStateData {
  const { address, isConnected } = useAccount();
  const [balance, setBalance] = useState<number>(0);
  const [accountValue, setAccountValue] = useState<number>(0);
  const [marginUsed, setMarginUsed] = useState<number>(0);
  const [positions, setPositions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isConnected || !address) {
      setBalance(0);
      setAccountValue(0);
      setMarginUsed(0);
      setPositions([]);
      return;
    }

    const fetchUserState = async () => {
      setIsLoading(true);
      try {
        const userState = await hyperliquidClient.getUserState(address);

        // Extract balance
        const withdrawable = parseFloat(userState.withdrawable);
        setBalance(withdrawable);

        // Extract account value
        const accountVal = parseFloat(userState.marginSummary.accountValue);
        setAccountValue(accountVal);

        // Extract margin used
        const margin = parseFloat(userState.marginSummary.totalMarginUsed);
        setMarginUsed(margin);

        // Extract active positions
        const activePositions = userState.assetPositions
          .filter(ap => parseFloat(ap.position.szi) !== 0)
          .map(ap => ({
            coin: ap.position.coin,
            szi: ap.position.szi,
            entryPx: ap.position.entryPx,
            unrealizedPnl: ap.position.unrealizedPnl,
            leverage: ap.position.leverage.value,
            liquidationPx: ap.position.liquidationPx,
            returnOnEquity: ap.position.returnOnEquity,
            marginUsed: ap.position.marginUsed,
            cumFunding: ap.position.cumFunding?.allTime || '0',
          }));

        setPositions(activePositions);
      } catch (error) {
        console.error('Error fetching user state:', error);
        setBalance(0);
        setAccountValue(0);
        setMarginUsed(0);
        setPositions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserState();

    // Poll every 10 seconds for all user state updates
    const interval = setInterval(fetchUserState, 10000);
    return () => clearInterval(interval);
  }, [address, isConnected]);

  return {
    balance,
    accountValue,
    marginUsed,
    positions,
    isLoading,
    isConnected,
  };
}
